import { EmployeeDetailPage, EmployeesPage } from "@/features/accounting/employees/EmployeesPage";
import { EmployeeMovementPage } from "@/features/accounting/employees/EmployeeMovementPage";
import { SetupWizardPage } from "@/features/settings/setup/SetupWizardPage";
import { StartPage } from "@/features/system/StartPage";
import { ReportMappingPage } from "@/features/reports/mapping/ReportMappingPage";
import { ReportTemplatesPage } from "@/features/reports/templates/ReportTemplatesPage";
import { TemplateEditorPage } from "@/features/reports/templates/TemplateEditorPage";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import type { ReactElement } from "react";
import { RouteError } from "@/deps/ui-library";
import { AppShell } from "@/shell/AppShell";
import { SessionProvider } from "./session";
import { SubscriptionProvider } from "./subscription";
import { SubscriptionPage } from "@/features/settings/subscription/SubscriptionPage";
import { CheckoutPage } from "@/features/settings/subscription/CheckoutPage";
import {
  PAYMENT_RETURN_PATH,
  PaymentReturnPage,
} from "@/features/settings/subscription/PaymentReturnPage";
import { ToastProvider } from "./toasts";
import { SCREENS } from "./screens";
import { PlannedScreen } from "@/components/PlannedScreen";
import { parseListSearch, type ListSearch } from "@/components/list/listQuery";
import { InvoicesListPage } from "@/features/sales/invoices/InvoicesListPage";
import { CreditNotesListPage } from "@/features/sales/credit-notes/CreditNotesListPage";
import { ReceiptsListPage } from "@/features/sales/receipts/ReceiptsListPage";
import { FinancialOverviewPage } from "@/features/overview/FinancialOverviewPage";
import { InvoiceDetailPage } from "@/features/sales/invoices/InvoiceDetailPage";
import { NewInvoicePage } from "@/features/sales/invoices/NewInvoicePage";
import { AnalysisPage } from "@/features/parties/AnalysisPage";
import { BillingItemsPage } from "@/features/sales/items/BillingItemsPage";
import { CustomerDetailPage } from "@/features/parties/CustomerDetailPage";
import { SupplierDetailPage } from "@/features/parties/SupplierDetailPage";
import { BankAccountsPage } from "@/features/banking/accounts/BankAccountsPage";
import { ReconciliationPage } from "@/features/banking/reconciliation/ReconciliationPage";
import type { StatementLineStart } from "@/features/banking/reconciliation/fromStatementLine";
import { PartiesPage } from "@/features/parties/PartiesPage";
import { JournalDetailPage } from "@/features/accounting/journals/JournalDetailPage";
import { DebitNoteDetailPage } from "@/features/purchases/debit-notes/DebitNoteDetailPage";
import { DebitNotesListPage } from "@/features/purchases/debit-notes/DebitNotesListPage";
import {
  EditBillPage,
  EditCreditNotePage,
  EditDebitNotePage,
  EditInvoicePage,
  EditPurchaseOrderPage,
  EditQuotationPage,
  NewDebitNoteRoute,
} from "@/features/system/EditDraftPages";
import { PaymentDetailPage } from "@/features/purchases/payments/PaymentDetailPage";
import { PaymentsListPage } from "@/features/purchases/payments/PaymentsListPage";
import { RecordPaymentPage } from "@/features/purchases/payments/RecordPaymentPage";
import { JournalEditorPage } from "@/features/accounting/journals/JournalEditorPage";
import { JournalsListPage } from "@/features/accounting/journals/JournalsListPage";
import { FundTransferDetailPage } from "@/features/banking/transfers/FundTransferDetailPage";
import { CreditNoteDetailPage } from "@/features/sales/credit-notes/CreditNoteDetailPage";
import { ReceiptDetailPage } from "@/features/sales/receipts/ReceiptDetailPage";
import { QuotationDetailPage } from "@/features/sales/quotations/QuotationDetailPage";
import { QuotationEditorPage } from "@/features/sales/quotations/QuotationEditorPage";
import { QuotationsListPage } from "@/features/sales/quotations/QuotationsListPage";
import { PurchaseOrderDetailPage } from "@/features/purchases/orders/PurchaseOrderDetailPage";
import { PurchaseOrderEditorPage } from "@/features/purchases/orders/PurchaseOrderEditorPage";
import { PurchaseOrdersListPage } from "@/features/purchases/orders/PurchaseOrdersListPage";
import { BillsListPage } from "@/features/purchases/bills/BillsListPage";
import { BillDetailPage } from "@/features/purchases/bills/BillDetailPage";
import { FundTransfersListPage } from "@/features/banking/transfers/FundTransfersListPage";
import { ApprovalsQueuePage } from "@/features/accounting/approvals/ApprovalsQueuePage";
import { AllReportsPage } from "@/features/reports/AllReportsPage";
import { PayablesAgeingPage, ReceivablesAgeingPage } from "@/features/reports/AgeingPage";
import { BalanceSheetPage } from "@/features/reports/BalanceSheetPage";
import { CashFlowPage } from "@/features/reports/CashFlowPage";
import { TdsWithheldPage } from "@/features/reports/TdsWithheldPage";
import { VatReturnPage } from "@/features/reports/VatReturnPage";
import { GeneralLedgerPage, type GeneralLedgerSearch } from "@/features/reports/GeneralLedgerPage";
import { ProfitAndLossPage } from "@/features/reports/ProfitAndLossPage";
import { TrialBalancePage } from "@/features/reports/TrialBalancePage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { PeriodControlPage } from "@/features/settings/PeriodControlPage";
import { YearEndPage } from "@/features/accounting/year-end/YearEndPage";
import { CountryPackPage } from "@/features/settings/CountryPackPage";
import { ApprovalRulesPage } from "@/features/settings/ApprovalRulesPage";
import { EmailTemplatesPage } from "@/features/settings/EmailTemplatesPage";
import { DocumentBrandingPage } from "@/features/settings/DocumentBrandingPage";
import { InvoicingSetupPage } from "@/features/settings/InvoicingSetupPage";
import { NewCreditNotePage } from "@/features/sales/credit-notes/NewCreditNotePage";
import { RecordReceiptPage } from "@/features/sales/receipts/RecordReceiptPage";
import { NewBillPage } from "@/features/purchases/bills/NewBillPage";
import { NewFundTransferPage } from "@/features/banking/transfers/NewFundTransferPage";
import { PrimitivesPage } from "@/components/__demo__/PrimitivesPage";
import { RequirePermission } from "@/components/RequirePermission";
import { DocumentLinkPage } from "@/features/system/DocumentLinkPage";
import { PlannedFeaturePage } from "@/features/system/PlannedFeaturePage";
import { NotFoundPage } from "@/features/system/NotFoundPage";
import { HelpPage } from "@/features/help/HelpPage";
import { HelpCategoryPage } from "@/features/help/HelpCategoryPage";
import { HelpArticlePage } from "@/features/help/HelpArticlePage";
import { SupportTicketsPage } from "@/features/help/SupportTicketsPage";
import { NewSupportTicketPage } from "@/features/help/NewSupportTicketPage";
import { SupportTicketPage } from "@/features/help/SupportTicketPage";

/**
 * Code-based TanStack Router tree. Paths are the product's information
 * architecture; each leaf renders a PlannedScreen until pass two swaps in the
 * real page component (the `module` in SCREENS says where it lives).
 */
const rootRoute = createRootRoute({
  component: () => (
    <ToastProvider>
      <SessionProvider fallback={<div className="min-h-screen bg-canvas" />}>
        {/* Outside the shell, not inside it: a suspended organisation gets the screen that
            explains the block *instead of* the application, from whatever route it was on. */}
        <SubscriptionProvider>
          <AppShell>
            <Outlet />
          </AppShell>
        </SubscriptionProvider>
      </SessionProvider>
    </ToastProvider>
  ),
  notFoundComponent: NotFoundPage,
});

// Generic over the path literal: `createRoute` must see '/sales/invoices', not `string`,
// or the literal never reaches the route tree and every typed `to` collapses to '/'.
const r = <TPath extends string>(path: TPath, component: () => ReactElement) =>
  createRoute({ getParentRoute: () => rootRoute, path, component });

const planned = (key: keyof typeof SCREENS) => () => <PlannedScreen spec={SCREENS[key]} />;

/**
 * A route whose filters live in the URL.
 *
 * `validateSearch` is what makes a filtered list a link somebody can send: the state is in the
 * address bar, the back button returns to the list you were looking at, and a hand-edited
 * parameter is dropped rather than passed through to the API.
 */
const listRoute = <TPath extends string>(path: TPath, component: () => ReactElement) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    component,
    validateSearch: (search: Record<string, unknown>): ListSearch => parseListSearch(search),
  });

/** A report whose date or month lives in the address bar. */
const dated = <TPath extends string>(path: TPath, component: () => ReactElement) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    component,
    validateSearch: (s: Record<string, unknown>) => {
      const text = (v: unknown) => (typeof v === "string" ? v : undefined);
      return {
        asOn: text(s.asOn),
        asAt: text(s.asAt),
        from: text(s.from),
        to: text(s.to),
        month: text(s.month),
      };
    },
  });

type CustomerSearch = { tab?: "open" | "details"; from?: string; to?: string };

/** A form opened from a customer's page starts with that customer. */
const startsFromCustomer = (s: Record<string, unknown>): { customer?: string } =>
  typeof s.customer === "string" ? { customer: s.customer } : {};

/** A form opened for a line of the bank's statement starts with its figures, and matches it once saved. */
const startsFromStatementLine = (s: Record<string, unknown>): Partial<StatementLineStart> =>
  typeof s.statementLine === "string" &&
  typeof s.account === "string" &&
  typeof s.date === "string" &&
  (typeof s.amount === "string" || typeof s.amount === "number")
    ? {
        statementLine: s.statementLine,
        account: s.account,
        date: s.date,
        // TanStack parses a numeric search value as a number.
        amount: typeof s.amount === "number" ? s.amount.toFixed(2) : s.amount,
      }
    : {};

/** A form opened from a supplier's page starts with that supplier. */
const startsFromSupplier = (s: Record<string, unknown>): { supplier?: string } =>
  typeof s.supplier === "string" ? { supplier: s.supplier } : {};

export const routes = {
  overview: r("/", FinancialOverviewPage),

  // Development only. `import.meta.env.DEV` is a compile-time constant, so the page and everything
  // it pulls in are dropped from a production bundle rather than merely unreachable in it.
  ...(import.meta.env.DEV ? { primitives: r("/dev/primitives", PrimitivesPage) } : {}),

  invoices: listRoute("/sales/invoices", InvoicesListPage),
  invoiceNew: createRoute({
    getParentRoute: () => rootRoute,
    path: "/sales/invoices/new",
    validateSearch: startsFromCustomer,
    component: () => (
      <RequirePermission
        permission="sales.invoices.create"
        backTo="/sales/invoices"
        backLabel="Back to invoices"
      >
        <NewInvoicePage />
      </RequirePermission>
    ),
  }),
  invoiceDetail: r("/sales/invoices/$id", InvoiceDetailPage),
  invoiceEdit: r("/sales/invoices/$id/edit", () => (
    <RequirePermission
      permission="sales.invoices.create"
      backTo="/sales/invoices"
      backLabel="Back to invoices"
    >
      <EditInvoicePage />
    </RequirePermission>
  )),
  quotations: listRoute("/sales/quotations", QuotationsListPage),
  quotationNew: createRoute({
    getParentRoute: () => rootRoute,
    path: "/sales/quotations/new",
    validateSearch: startsFromCustomer,
    component: () => (
      <RequirePermission
        permission="sales.quotations.create"
        backTo="/sales/quotations"
        backLabel="Back to quotations"
      >
        <QuotationEditorPage />
      </RequirePermission>
    ),
  }),
  quotationDetail: r("/sales/quotations/$id", QuotationDetailPage),
  quotationEdit: r("/sales/quotations/$id/edit", () => (
    <RequirePermission
      permission="sales.quotations.create"
      backTo="/sales/quotations"
      backLabel="Back to quotations"
    >
      <EditQuotationPage />
    </RequirePermission>
  )),
  purchaseOrders: listRoute("/purchases/orders", PurchaseOrdersListPage),
  purchaseOrderNew: createRoute({
    getParentRoute: () => rootRoute,
    path: "/purchases/orders/new",
    validateSearch: startsFromSupplier,
    component: () => (
      <RequirePermission
        permission="purchases.orders.create"
        backTo="/purchases/orders"
        backLabel="Back to purchase orders"
      >
        <PurchaseOrderEditorPage />
      </RequirePermission>
    ),
  }),
  purchaseOrderDetail: r("/purchases/orders/$id", PurchaseOrderDetailPage),
  purchaseOrderEdit: r("/purchases/orders/$id/edit", () => (
    <RequirePermission
      permission="purchases.orders.create"
      backTo="/purchases/orders"
      backLabel="Back to purchase orders"
    >
      <EditPurchaseOrderPage />
    </RequirePermission>
  )),
  creditNotes: listRoute("/sales/credit-notes", CreditNotesListPage),
  creditNoteNew: r("/sales/credit-notes/new", () => (
    <RequirePermission
      permission="sales.creditnotes.create"
      backTo="/sales/credit-notes"
      backLabel="Back to credit notes"
    >
      <NewCreditNotePage />
    </RequirePermission>
  )),
  creditNoteDetail: r("/sales/credit-notes/$id", CreditNoteDetailPage),
  creditNoteEdit: r("/sales/credit-notes/$id/edit", () => (
    <RequirePermission
      permission="sales.creditnotes.create"
      backTo="/sales/credit-notes"
      backLabel="Back to credit notes"
    >
      <EditCreditNotePage />
    </RequirePermission>
  )),
  receipts: listRoute("/sales/receipts", ReceiptsListPage),
  receiptNew: createRoute({
    getParentRoute: () => rootRoute,
    path: "/sales/receipts/new",
    validateSearch: (s: Record<string, unknown>) => ({
      ...startsFromCustomer(s),
      ...startsFromStatementLine(s),
    }),
    component: () => (
      <RequirePermission
        permission="sales.receipts.create"
        backTo="/sales/receipts"
        backLabel="Back to receipts"
      >
        <RecordReceiptPage />
      </RequirePermission>
    ),
  }),
  receiptDetail: r("/sales/receipts/$id", ReceiptDetailPage),

  bills: listRoute("/purchases/bills", BillsListPage),
  billNew: createRoute({
    getParentRoute: () => rootRoute,
    path: "/purchases/bills/new",
    validateSearch: startsFromSupplier,
    component: () => (
      <RequirePermission
        permission="purchases.bills.create"
        backTo="/purchases/bills"
        backLabel="Back to bills"
      >
        <NewBillPage />
      </RequirePermission>
    ),
  }),
  billDetail: r("/purchases/bills/$id", BillDetailPage),
  billEdit: r("/purchases/bills/$id/edit", () => (
    <RequirePermission
      permission="purchases.bills.create"
      backTo="/purchases/bills"
      backLabel="Back to bills"
    >
      <EditBillPage />
    </RequirePermission>
  )),

  customers: r("/sales/customers", () => <PartiesPage kind="customer" />),
  customerDetail: createRoute({
    getParentRoute: () => rootRoute,
    path: "/sales/customers/$id",
    component: CustomerDetailPage,
    validateSearch: (s: Record<string, unknown>): CustomerSearch => ({
      ...(s.tab === "open" || s.tab === "details" ? { tab: s.tab } : {}),
      ...(typeof s.from === "string" ? { from: s.from } : {}),
      ...(typeof s.to === "string" ? { to: s.to } : {}),
    }),
  }),
  billingItems: r("/sales/billing-items", BillingItemsPage),
  suppliers: r("/purchases/suppliers", () => <PartiesPage kind="supplier" />),
  receivableAnalysis: r("/sales/receivable-analysis", () => (
    <RequirePermission
      permission="reports.view"
      backTo="/sales/customers"
      backLabel="Back to customers"
    >
      <AnalysisPage kind="customer" />
    </RequirePermission>
  )),
  payableAnalysis: r("/purchases/payable-analysis", () => (
    <RequirePermission
      permission="reports.view"
      backTo="/purchases/suppliers"
      backLabel="Back to suppliers"
    >
      <AnalysisPage kind="supplier" />
    </RequirePermission>
  )),
  supplierDetail: createRoute({
    getParentRoute: () => rootRoute,
    path: "/purchases/suppliers/$id",
    component: SupplierDetailPage,
    validateSearch: (s: Record<string, unknown>): CustomerSearch => ({
      ...(s.tab === "open" || s.tab === "details" ? { tab: s.tab } : {}),
      ...(typeof s.from === "string" ? { from: s.from } : {}),
      ...(typeof s.to === "string" ? { to: s.to } : {}),
    }),
  }),
  bankAccounts: r("/banking/accounts", BankAccountsPage),
  reconciliation: createRoute({
    getParentRoute: () => rootRoute,
    path: "/banking/reconciliation",
    validateSearch: (
      s: Record<string, unknown>,
    ): { account?: string; asAt?: string; problem?: string } => ({
      ...(typeof s.account === "string" ? { account: s.account } : {}),
      ...(typeof s.asAt === "string" ? { asAt: s.asAt } : {}),
      ...(typeof s.problem === "string" ? { problem: s.problem } : {}),
    }),
    component: () => (
      <RequirePermission
        permission="banking.reconciliation.view"
        backTo="/banking/accounts"
        backLabel="Back to bank accounts"
      >
        <ReconciliationPage />
      </RequirePermission>
    ),
  }),
  transfers: listRoute("/banking/transfers", FundTransfersListPage),
  transferNew: r("/banking/transfers/new", () => (
    <RequirePermission
      permission="banking.transfers.create"
      backTo="/banking/transfers"
      backLabel="Back to fund transfers"
    >
      <NewFundTransferPage />
    </RequirePermission>
  )),
  transferDetail: r("/banking/transfers/$id", FundTransferDetailPage),

  debitNotes: listRoute("/purchases/debit-notes", DebitNotesListPage),
  debitNoteNew: createRoute({
    getParentRoute: () => rootRoute,
    path: "/purchases/debit-notes/new",
    validateSearch: (s: Record<string, unknown>) => ({
      bill: typeof s.bill === "string" ? s.bill : undefined,
    }),
    component: () => (
      <RequirePermission
        permission="purchases.debitnotes.create"
        backTo="/purchases/debit-notes"
        backLabel="Back to debit notes"
      >
        <NewDebitNoteRoute />
      </RequirePermission>
    ),
  }),
  debitNoteDetail: r("/purchases/debit-notes/$id", DebitNoteDetailPage),
  debitNoteEdit: r("/purchases/debit-notes/$id/edit", () => (
    <RequirePermission
      permission="purchases.debitnotes.create"
      backTo="/purchases/debit-notes"
      backLabel="Back to debit notes"
    >
      <EditDebitNotePage />
    </RequirePermission>
  )),
  payments: listRoute("/purchases/payments", PaymentsListPage),
  paymentNew: createRoute({
    getParentRoute: () => rootRoute,
    path: "/purchases/payments/new",
    validateSearch: (s: Record<string, unknown>) => ({
      bill: typeof s.bill === "string" ? s.bill : undefined,
      ...startsFromSupplier(s),
      ...startsFromStatementLine(s),
    }),
    component: () => (
      <RequirePermission
        permission="purchases.payments.create"
        backTo="/purchases/payments"
        backLabel="Back to payments"
      >
        <RecordPaymentPage />
      </RequirePermission>
    ),
  }),
  paymentDetail: r("/purchases/payments/$id", PaymentDetailPage),

  approvals: r("/accounting/approvals", ApprovalsQueuePage),
  journals: listRoute("/accounting/journals", JournalsListPage),
  journalNew: createRoute({
    getParentRoute: () => rootRoute,
    path: "/accounting/journals/new",
    validateSearch: (
      s: Record<string, unknown>,
    ): { date?: string; narration?: string; adjusting?: boolean } => ({
      ...(typeof s.date === "string" ? { date: s.date } : {}),
      ...(typeof s.narration === "string" ? { narration: s.narration } : {}),
      ...(s.adjusting === true ? { adjusting: true } : {}),
    }),
    component: () => (
      <RequirePermission
        permission="accounting.journals.create"
        backTo="/accounting/journals"
        backLabel="Back to journals"
      >
        <JournalEditorPage />
      </RequirePermission>
    ),
  }),
  journalDetail: r("/accounting/journals/$id", JournalDetailPage),
  journalEdit: r("/accounting/journals/$id/edit", () => (
    <RequirePermission
      permission="accounting.journals.create"
      backTo="/accounting/journals"
      backLabel="Back to journals"
    >
      <JournalEditorPage />
    </RequirePermission>
  )),
  start: r("/start", () => (
    <RequirePermission permission="settings.company.manage" backTo="/" backLabel="Back to overview">
      <StartPage />
    </RequirePermission>
  )),
  setup: r("/setup", () => (
    <RequirePermission permission="settings.company.manage" backTo="/" backLabel="Back to overview">
      <SetupWizardPage />
    </RequirePermission>
  )),
  reportMapping: r("/reports/mapping", () => (
    <RequirePermission permission="reports.view" backTo="/reports" backLabel="Back to reports">
      <ReportMappingPage />
    </RequirePermission>
  )),
  reportTemplates: r("/reports/templates", () => (
    <RequirePermission permission="reports.view" backTo="/reports" backLabel="Back to reports">
      <ReportTemplatesPage />
    </RequirePermission>
  )),
  reportTemplate: r("/reports/templates/$code", () => (
    <RequirePermission permission="reports.view" backTo="/reports" backLabel="Back to reports">
      <TemplateEditorPage />
    </RequirePermission>
  )),
  employees: r("/accounting/employees", () => (
    <RequirePermission permission="accounting.employees.view" backTo="/" backLabel="Back">
      <EmployeesPage />
    </RequirePermission>
  )),
  employeeMovement: r("/accounting/employees/movements/$id", () => (
    <RequirePermission
      permission="accounting.employees.view"
      backTo="/accounting/employees"
      backLabel="Back to employee ledgers"
    >
      <EmployeeMovementPage />
    </RequirePermission>
  )),
  employeeDetail: createRoute({
    getParentRoute: () => rootRoute,
    path: "/accounting/employees/$id",
    component: () => (
      <RequirePermission
        permission="accounting.employees.view"
        backTo="/accounting/employees"
        backLabel="Back"
      >
        <EmployeeDetailPage />
      </RequirePermission>
    ),
  }),
  yearEnd: r("/accounting/year-end", () => (
    <RequirePermission permission="accounting.periods.view" backTo="/" backLabel="Back home">
      <YearEndPage />
    </RequirePermission>
  )),
  periods: r("/accounting/periods", () => (
    <RequirePermission permission="accounting.periods.view" backTo="/" backLabel="Back to overview">
      <PeriodControlPage />
    </RequirePermission>
  )),
  allReports: r("/reports", AllReportsPage),
  balanceSheet: dated("/reports/balance-sheet", BalanceSheetPage),
  cashFlow: dated("/reports/cash-flow", CashFlowPage),
  receivablesAgeing: dated("/reports/receivables-ageing", ReceivablesAgeingPage),
  payablesAgeing: dated("/reports/payables-ageing", PayablesAgeingPage),
  vatReturn: dated("/reports/vat-return", VatReturnPage),
  tdsWithheld: dated("/reports/tds-withheld", TdsWithheldPage),
  profitAndLoss: r("/reports/profit-and-loss", ProfitAndLossPage),
  trialBalance: createRoute({
    getParentRoute: () => rootRoute,
    path: "/reports/trial-balance",
    component: TrialBalancePage,
    validateSearch: (s: Record<string, unknown>) => ({
      asOn: typeof s.asOn === "string" ? s.asOn : undefined,
      type: typeof s.type === "string" ? s.type : undefined,
    }),
  }),
  generalLedger: createRoute({
    getParentRoute: () => rootRoute,
    path: "/reports/general-ledger",
    component: GeneralLedgerPage,
    validateSearch: (s: Record<string, unknown>): GeneralLedgerSearch => {
      const text = (v: unknown) =>
        typeof v === "string" || typeof v === "number" ? String(v) : undefined;
      return {
        account: text(s.account),
        from: text(s.from),
        to: text(s.to),
        q: text(s.q),
        party: text(s.party),
        min: text(s.min),
      };
    },
  }),

  settings: createRoute({
    getParentRoute: () => rootRoute,
    path: "/settings",
    validateSearch: (s: Record<string, unknown>) => ({
      tab: typeof s.tab === "string" ? s.tab : undefined,
    }),
    component: () => (
      <RequirePermission
        permission="settings.company.manage"
        backTo="/"
        backLabel="Back to overview"
      >
        <SettingsPage />
      </RequirePermission>
    ),
  }),

  invoicingSetup: r("/settings/invoicing", () => (
    <RequirePermission
      permission="settings.company.manage"
      backTo="/settings"
      backLabel="Back to settings"
    >
      <InvoicingSetupPage />
    </RequirePermission>
  )),

  documentBranding: r("/settings/branding", () => (
    <RequirePermission
      permission="settings.company.manage"
      backTo="/settings"
      backLabel="Back to settings"
    >
      <DocumentBrandingPage />
    </RequirePermission>
  )),

  subscriptionSettings: r("/settings/subscription", SubscriptionPage),
  subscriptionCheckout: r("/settings/subscription/checkout", () => (
    <RequirePermission
      permission="settings.subscription.manage"
      backTo="/settings/subscription"
      backLabel="Back to subscription"
    >
      <CheckoutPage />
    </RequirePermission>
  )),
  // Back from eSewa, Khalti or connectIPS (suite ADR-0102). The locked-books page shows it too (app/subscription.tsx).
  subscriptionPaid: r(PAYMENT_RETURN_PATH, () => (
    <RequirePermission
      permission="settings.subscription.manage"
      backTo="/settings/subscription"
      backLabel="Back to subscription"
    >
      <PaymentReturnRoute />
    </RequirePermission>
  )),
  approvalRules: r("/settings/approval-rules", () => (
    <RequirePermission
      permission="settings.audit.view"
      backTo="/settings"
      backLabel="Back to settings"
    >
      <ApprovalRulesPage />
    </RequirePermission>
  )),
  emailTemplates: r("/settings/email-templates", () => (
    <RequirePermission
      permission="settings.company.manage"
      backTo="/settings"
      backLabel="Back to settings"
    >
      <EmailTemplatesPage />
    </RequirePermission>
  )),

  countryPack: r("/settings/localization", () => (
    <RequirePermission
      permission="settings.tax.view"
      backTo="/settings"
      backLabel="Back to settings"
    >
      <CountryPackPage />
    </RequirePermission>
  )),

  // Help (ADR-0010). Each screen asks the API whether Help is on and explains itself when it is not;
  // the ticket screens additionally need support.tickets.manage, which the API enforces as well.
  help: r("/help", HelpPage),
  helpCategory: r("/help/categories/$slug", () => <HelpCategoryRoute />),
  helpArticle: r("/help/articles/$slug", () => <HelpArticleRoute />),
  supportTickets: r("/help/tickets", () => (
    <RequirePermission
      permission="support.tickets.manage"
      backTo="/help"
      backLabel="Back to the guides"
    >
      <SupportTicketsPage />
    </RequirePermission>
  )),
  supportTicketNew: r("/help/tickets/new", () => (
    <RequirePermission
      permission="support.tickets.manage"
      backTo="/help"
      backLabel="Back to the guides"
    >
      <NewSupportTicketPage />
    </RequirePermission>
  )),
  supportTicket: r("/help/tickets/$id", () => (
    <RequirePermission
      permission="support.tickets.manage"
      backTo="/help"
      backLabel="Back to the guides"
    >
      <SupportTicketRoute />
    </RequirePermission>
  )),

  /** Nav targets that live in other design files (Quotations, Customers, …). */
  plannedFeature: r("/planned/$feature", PlannedFeaturePage),
  documentLink: r("/doc/$number", DocumentLinkPage),
};

// A tuple, not `Object.values(routes)`: addChildren builds the typed route table from
// the element types positionally, and an array-of-union erases every path but the common ones.
const routeTree = rootRoute.addChildren([
  routes.overview,
  routes.invoices,
  routes.invoiceNew,
  routes.invoiceDetail,
  routes.invoiceEdit,
  routes.quotations,
  routes.quotationNew,
  routes.quotationDetail,
  routes.quotationEdit,
  routes.purchaseOrders,
  routes.purchaseOrderNew,
  routes.purchaseOrderDetail,
  routes.purchaseOrderEdit,
  routes.creditNotes,
  routes.creditNoteNew,
  routes.creditNoteDetail,
  routes.creditNoteEdit,
  routes.receipts,
  routes.receiptNew,
  routes.receiptDetail,
  routes.bills,
  routes.billNew,
  routes.billDetail,
  routes.billEdit,
  routes.debitNotes,
  routes.debitNoteNew,
  routes.debitNoteDetail,
  routes.debitNoteEdit,
  routes.payments,
  routes.paymentNew,
  routes.paymentDetail,
  routes.customers,
  routes.customerDetail,
  routes.billingItems,
  routes.suppliers,
  routes.receivableAnalysis,
  routes.payableAnalysis,
  routes.supplierDetail,
  routes.employees,
  routes.employeeDetail,
  routes.setup,
  routes.start,
  routes.reportMapping,
  routes.reportTemplates,
  routes.reportTemplate,
  routes.employeeMovement,
  routes.bankAccounts,
  routes.reconciliation,
  routes.transfers,
  routes.transferNew,
  routes.transferDetail,
  routes.approvals,
  routes.journals,
  routes.journalNew,
  routes.yearEnd,
  routes.journalDetail,
  routes.journalEdit,
  routes.periods,
  routes.allReports,
  routes.balanceSheet,
  routes.cashFlow,
  routes.receivablesAgeing,
  routes.payablesAgeing,
  routes.vatReturn,
  routes.tdsWithheld,
  routes.profitAndLoss,
  routes.trialBalance,
  routes.generalLedger,
  routes.settings,
  routes.invoicingSetup,
  routes.countryPack,
  routes.emailTemplates,
  routes.approvalRules,
  routes.subscriptionSettings,
  routes.subscriptionCheckout,
  routes.subscriptionPaid,
  routes.documentBranding,
  routes.help,
  routes.helpCategory,
  routes.helpArticle,
  routes.supportTickets,
  routes.supportTicketNew,
  routes.supportTicket,
  routes.plannedFeature,
  routes.documentLink,
]);

// The Help screens take their slug or id as a prop, so they can be rendered without this tree.
function HelpCategoryRoute() {
  return <HelpCategoryPage slug={useParams({ from: "/help/categories/$slug" }).slug} />;
}
function HelpArticleRoute() {
  return <HelpArticlePage slug={useParams({ from: "/help/articles/$slug" }).slug} />;
}
function SupportTicketRoute() {
  return <SupportTicketPage id={useParams({ from: "/help/tickets/$id" }).id} />;
}
function PaymentReturnRoute() {
  const navigate = useNavigate();
  return <PaymentReturnPage onDone={() => void navigate({ to: "/settings/subscription" })} />;
}

export const router = createRouter({
  routeTree,
  defaultErrorComponent: RouteError,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
